import type { ActionCommand, MatchState } from "../../../schemas"
import { handleAttack } from "./commands/attack"
import { handleConcede } from "./commands/concede"
import { handleEndTurn } from "./commands/end-turn"
import { handleLevelUpHero } from "./commands/level-up-hero"
import { handlePlayCard } from "./commands/play-card"
import { handleRecruitCard } from "./commands/recruit-card"
import { handleSwapActiveUnit } from "./commands/swap-active-unit"
import { handleUseAbility } from "./commands/use-ability"
import type { CommandResult, HeroLookup } from "./state"
import { err } from "./state"

export type ExecuteOptions = {
  heroes?: HeroLookup
}

/**
 * Execute a command against the current match state.
 * Returns a CommandResult (ok: true with new state, or ok: false with error).
 */
export function executeCommand(
  state: MatchState,
  command: ActionCommand,
  options: ExecuteOptions = {},
): CommandResult {
  // Validate match is still active
  if (state.status === "completed") {
    return err("MATCH_COMPLETED", "Match is already completed.")
  }

  // Validate it is the correct seat's turn
  if (command.seatId !== state.activeSeat) {
    return err(
      "WRONG_SEAT",
      `It is ${state.activeSeat}'s turn, not ${command.seatId}'s.`,
    )
  }

  // Validate turn number matches
  if (command.turnNumber !== state.turn.number) {
    return err(
      "WRONG_TURN",
      `Command is for turn ${command.turnNumber} but current turn is ${state.turn.number}.`,
    )
  }

  // Dispatch to specific handler
  switch (command.kind) {
    case "play_card":
      return handlePlayCard(state, command)

    case "attack":
      return handleAttack(state, command)

    case "use_ability":
      return handleUseAbility(state, command)

    case "swap_active_unit":
      return handleSwapActiveUnit(state, command)

    case "recruit_card":
      return handleRecruitCard(state, command)

    case "level_up_hero":
      return handleLevelUpHero(state, command, options.heroes ?? new Map())

    case "end_turn":
      return handleEndTurn(state, command)

    case "concede":
      return handleConcede(state, command)

    default: {
      const exhaustive: never = command
      return err(
        "UNKNOWN_COMMAND",
        `Unknown command kind: ${(exhaustive as ActionCommand).kind}`,
      )
    }
  }
}
